import { adminLinks, instructorLinks, memberLinks } from './links';

/**
 * Global search. The database function search() runs as the signed-in person, so
 * it only ever returns rows they could already open: this file only decides how
 * to show a result and where it links.
 */
export type SearchKind = 'person' | 'household' | 'payment' | 'refund' | 'event' | 'dojo';
export interface SearchMeta {
  household_id?: string; dojo_id?: string | null; payment_id?: string; member_number?: number | null;
  slug?: string; published?: boolean;
}
export interface SearchResult { kind: SearchKind; id: string; title: string; subtitle: string | null; detail: string | null; rank: number; meta: SearchMeta }
/** Who is looking, which decides where a result links. */
export interface Viewer { isStaff: boolean; instructorDojoIds: string[]; householdId: string | null }

/** Two characters, or any number of digits (a member number). */
export const shouldSearch = (q: string) => { const t = q.trim(); return t.length >= 2 || /^\d+$/.test(t); };

export function resultHref(r: SearchResult, v: Viewer): string {
  const m = r.meta ?? {};
  if (v.isStaff) {
    switch (r.kind) {
      case 'person': return adminLinks.person(r.id);
      case 'household': return adminLinks.household(r.id);
      case 'payment': case 'refund': return adminLinks.payment(r.id);
      case 'event': return adminLinks.event(r.id);
      case 'dojo': return adminLinks.dojo(r.id);
    }
  }
  switch (r.kind) {
    case 'person':
      if (m.household_id && m.household_id === v.householdId) return memberLinks.person(r.id);
      if (m.dojo_id && v.instructorDojoIds.includes(m.dojo_id)) return instructorLinks.student(r.id);
      return memberLinks.household();
    case 'household': case 'payment': case 'refund': return memberLinks.household();
    case 'event': return memberLinks.event(m.slug ?? '');
    case 'dojo': return v.instructorDojoIds.includes(r.id) ? instructorLinks.dojo() : memberLinks.dojo(r.id);
  }
}

const GROUPS: { kinds: SearchKind[]; label: string; memberLabel?: string }[] = [
  { kinds: ['person'], label: 'People', memberLabel: 'People' },
  { kinds: ['household'], label: 'Households', memberLabel: 'Your household' },
  { kinds: ['payment', 'refund'], label: 'Payments' },
  { kinds: ['event'], label: 'Events' },
  { kinds: ['dojo'], label: 'Dojos' },
];

/** Results grouped by kind, groups in a fixed order, results in rank order within each. */
export function groupResults(rows: SearchResult[], isStaff = true) {
  return GROUPS
    .map(g => ({ key: g.kinds[0], label: !isStaff && g.memberLabel ? g.memberLabel : g.label, items: rows.filter(r => g.kinds.includes(r.kind)) }))
    .filter(g => g.items.length > 0);
}

